import Button from '@/features/shared/Button';
import Image from '@/features/shared/Image';
import {
  MuiButtonBase,
  MuiCard,
  MuiCardActions,
  MuiCardContent,
  MuiCardMedia,
  MuiTypography,
} from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { getPageUrl, mapLinkToProps } from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import React, { memo, useContext } from 'react';
import useStyles from './SingleContentCard.styles';

const SingleContentCard = ({
  data,
  cardColor,
  theme,
  className: overrideClassName,
  elevation = 8,
}: {
  data?: CardType;
  cardColor?: string;
  theme?: ThemeType;
  className?: string;
  elevation?: number;
}): JSX.Element => {
  const page = useContext(BrPageContext);
  const classes = useStyles({});
  const cardClassName = clsx(
    'gntc-single-content-card',
    classes.card,
    `bg-color-${cardColor}`,
    `${theme}-theme`,
    overrideClassName,
    'gtm-card-carousel-card',
  );
  const { cardImage, cardLogo, cardDescription, cardHeading, buttonLink, cardCategory, url } =
    data || {};
  const { buttonStyle } = buttonLink || {};

  const buttonLinkProps = {
    link: buttonLink,
    outlined: /secondary/.test(buttonStyle || '') || undefined,
    variant: /highlighted/.test(buttonStyle || '') ? 'link' : 'button',
    theme,
  };
  const cardHref = page && getPageUrl({ url: url || '', page });
  const cardLinkProps = mapLinkToProps({ href: cardHref });

  return (
    <MuiCard className={cardClassName} elevation={elevation}>
      {url && (
        <MuiButtonBase
          {...cardLinkProps}
          className={clsx(classes.cardLink, 'gtm-card-carousel-cta')}
        />
      )}
      {cardImage && (
        <MuiCardMedia className={classes.image}>
          <Image image={cardImage} type="card" roundedCorners={false} />
        </MuiCardMedia>
      )}
      {(cardLogo || cardHeading || cardDescription || cardCategory) && (
        <MuiCardContent className={classes.content}>
          {cardCategory && (
            <MuiTypography variant="overline" className={classes.contentCategory}>
              {cardCategory.label || cardCategory}
            </MuiTypography>
          )}
          {cardHeading && (
            <MuiTypography
              className={clsx(classes.contentHeading, 'gtm-card-carousel-title')}
              variant="h6"
              dangerouslySetInnerHTML={{ __html: cardHeading }}
            />
          )}
          {cardDescription && (
            <CaptureClicksRichText
              theme={theme}
              className={classes.copy}
              content={cardDescription}
            />
          )}
        </MuiCardContent>
      )}
      {buttonLink && (
        <MuiCardActions className={classes.actions}>
          <Button
            {...buttonLinkProps}
            className={clsx('gtm-card-carousel-cta', classes.cardActionsLink)}
          />
        </MuiCardActions>
      )}
    </MuiCard>
  );
};

export default memo(SingleContentCard);
